import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AuthModal } from '@/components/AuthModal';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronLeft, CheckCircle2, Info,
  Calendar, Scale, Ruler, Users, Zap, Target, Leaf, Utensils,
  Clock, BarChart2, Gauge, Award, TrendingDown, TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';

const QUIZ_BREADCRUMBS = [{ label: 'TDEE Калкулатор', href: '/calculator' }];
const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Step Illustrations ────────────────────────────────────────────────────────
const STEP_META = [
  { icon: Calendar,  color: '#34d399', bg: '#34d39918' },
  { icon: Scale,     color: '#60a5fa', bg: '#60a5fa18' },
  { icon: Ruler,     color: '#f59e0b', bg: '#f59e0b18' },
  { icon: Users,     color: '#e879f9', bg: '#e879f918' },
  { icon: Zap,       color: '#f97316', bg: '#f9731618' },
  { icon: Target,    color: '#34d399', bg: '#34d39918' },
  { icon: Leaf,      color: '#4ade80', bg: '#4ade8018' },
  { icon: Utensils,  color: '#f43f5e', bg: '#f43f5e18' },
  { icon: Clock,     color: '#a78bfa', bg: '#a78bfa18' },
  { icon: BarChart2, color: '#22d3ee', bg: '#22d3ee18' },
  { icon: Gauge,     color: '#fb923c', bg: '#fb923c18' },
];

const StepIllustration = ({ step }) => {
  const meta = STEP_META[step - 1];
  if (!meta) return null;
  const IconComp = meta.icon;
  return (
    <div className="flex justify-center mb-4">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: meta.bg }}
      >
        <IconComp style={{ color: meta.color }} className="w-8 h-8" strokeWidth={1.5} />
      </div>
    </div>
  );
};

// ─── BMI helper (used in step 12) ─────────────────────────────────────────────
const getBmiInfo = (weight, height) => {
  if (!weight || !height) return null;
  const bmi = parseFloat((weight / Math.pow(height / 100, 2)).toFixed(1));
  let category, color;
  if (bmi < 18.5)      { category = 'Поднормено тегло'; color = '#60a5fa'; }
  else if (bmi < 25)   { category = 'Нормално тегло';   color = '#34d399'; }
  else if (bmi < 30)   { category = 'Наднормено тегло'; color = '#f59e0b'; }
  else                 { category = 'Затлъстяване';      color = '#f43f5e'; }
  const pct = Math.min(100, Math.max(0, ((bmi - 15) / 25) * 100));
  return { bmi, category, color, pct };
};

// ─── Data ────────────────────────────────────────────────────────────────────
const ACTIVITIES = [
  { value: 'sedentary',   label: 'Заседнал живот',    desc: 'Бюро работа, без спорт' },
  { value: 'light',       label: 'Лека активност',     desc: '1–3 дни спорт седмично' },
  { value: 'moderate',    label: 'Умерена активност',  desc: '3–5 дни спорт седмично' },
  { value: 'active',      label: 'Активен',            desc: '6–7 дни спорт седмично' },
  { value: 'very_active', label: 'Много активен',      desc: 'Физическа работа / интензивни тренировки' },
];

const GOALS = [
  { value: 'lose',     label: 'Отслабване', desc: 'Искам да отпадна в тегло',        color: 'emerald' },
  { value: 'maintain', label: 'Поддържане', desc: 'Искам да запазя теглото си',       color: 'blue'    },
  { value: 'gain',     label: 'Качване',    desc: 'Искам да направя мускулна маса',   color: 'purple'  },
];

const PACES = [
  { value: 'slow',   label: 'Бавно',   desc: '~0.15 кг/седм.' },
  { value: 'medium', label: 'Умерено', desc: '~0.3 кг/седм.'  },
  { value: 'fast',   label: 'Бързо',   desc: '~0.5 кг/седм.'  },
];

const DIETS = [
  { value: 'omnivore', label: 'Всеядни', desc: 'Месо, риба, млечни, яйца — без ограничения' },
  { value: 'vegan',    label: 'Веган',   desc: 'Само растителни продукти — без месо, риба, млечни и яйца' },
];

const MEAL_PATTERNS = [
  { value: 'standard', label: 'Стандартно (3 хранения + снак)', desc: 'Закуска, обяд, вечеря и снак — равномерно разпределени' },
  { value: 'fasting',  label: 'Без закуска / Фастинг',          desc: 'Обяд, вечеря и снак — пропускаш закуска (16:8 протокол)' },
];

const getFoodGroups = (diet) => {
  const isVegan = diet === 'vegan';
  return [
    {
      label: 'Белтъци', headerClass: 'text-amber-700', dividerBg: 'bg-amber-100',
      foods: isVegan
        ? ['Тофу', 'Темпе', 'Леща', 'Нахут', 'Черен боб', 'Едамаме', 'Конопено семе']
        : ['Пилешко', 'Пуешко', 'Говеждо', 'Риба тон', 'Сьомга', 'Треска', 'Яйца', 'Извара', 'Гръцко кисело мляко', 'Кисело мляко', 'Бяло сирене'],
    },
    {
      label: 'Въглехидрати', headerClass: 'text-sky-700', dividerBg: 'bg-sky-100',
      foods: ['Бял ориз', 'Паста', 'Картофи', 'Сладки картофи', 'Пълнозърнест хляб', 'Бял хляб', 'Овесени ядки', 'Банан', 'Ябълка', 'Боб', 'Нахут', 'Леща'],
    },
    {
      label: 'Мазнини', headerClass: 'text-emerald-700', dividerBg: 'bg-emerald-100',
      foods: isVegan
        ? ['Зехтин', 'Орехи', 'Бадеми', 'Авокадо', 'Тахан', 'Фъстъчено масло', 'Кокосово масло']
        : ['Зехтин', 'Масло', 'Орехи', 'Бадеми', 'Фъстъчено масло', 'Мед'],
    },
  ];
};

const METRIC_OPTIONS = [
  { value: 'waist_cm',  label: 'Талия'            },
  { value: 'chest_cm',  label: 'Гръдна обиколка'  },
  { value: 'bicep_cm',  label: 'Ръка / Бицепс'    },
  { value: 'thigh_cm',  label: 'Крак / Бедро'      },
];

// ─── Small shared components ──────────────────────────────────────────────────
const RadioOption = ({ val, label, desc, current, onChange }) => (
  <button type="button" onClick={() => onChange(val)}
    className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
      current === val
        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
        : 'border-[#E6EDF5] dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-emerald-300 dark:hover:border-emerald-500/60'
    }`}>
    <div className="flex items-center gap-3">
      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
        current === val ? 'border-emerald-500 bg-emerald-600' : 'border-slate-300 dark:border-slate-500'
      }`}>
        {current === val && <div className="w-full h-full rounded-full bg-white scale-50" />}
      </div>
      <div>
        <p className="font-medium text-gray-900 dark:text-gray-100">{label}</p>
        {desc && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{desc}</p>}
      </div>
    </div>
  </button>
);

const NumberInput = ({ label, value, onChange, min, max, unit, why }) => (
  <div className="space-y-3">
    <label className="block text-base font-medium text-gray-900 dark:text-gray-100">{label}</label>
    {why && <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" />{why}</p>}
    <div className="flex items-center gap-3">
      <input
        type="number" value={value} onChange={e => onChange(e.target.value)}
        min={min} max={max} autoFocus
        className="flex-1 h-12 px-4 rounded-lg border-2 border-[#E6EDF5] dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 text-lg font-medium focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors"
        placeholder={`${min}–${max}`}
      />
      <span className="text-slate-500 dark:text-slate-400 font-medium text-sm">{unit}</span>
    </div>
  </div>
);

const FoodChip = ({ food, excluded, onToggle }) => (
  <button type="button" onClick={() => onToggle(food)} data-testid={`food-chip-${food}`}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 select-none ${
      excluded
        ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-700/40 text-rose-400 line-through opacity-80'
        : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700'
    }`}>
    {food}
  </button>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function QuizPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedPace, setSelectedPace] = useState('medium');
  const [form, setForm] = useState({
    age: '', weight: '', height: '', gender: 'male', activity: '', goal: '', diet: 'omnivore',
    excludedFoods: [], meal_pattern: 'standard',
    trackProgress: null, trackedMetrics: [],
  });

  const totalSteps = 11;
  const progress = (step / totalSteps) * 100;

  const STEP_NAMES = [
    'Възраст', 'Тегло', 'Ръст', 'Пол', 'Активност', 'Цел',
    'Режим', 'Предпочитания', 'Хранения', 'Проследяване', 'Темпо',
  ];

  const toggleFood = (food) => setForm(f => ({
    ...f,
    excludedFoods: f.excludedFoods.includes(food)
      ? f.excludedFoods.filter(x => x !== food)
      : [...f.excludedFoods, food],
  }));

  const normalizeNum = (val) => Number(String(val).replace(',', '.'));

  const canContinue = () => {
    const age    = normalizeNum(form.age);
    const weight = normalizeNum(form.weight);
    const height = normalizeNum(form.height);
    if (step === 1)  return age >= 16 && age <= 80;
    if (step === 2)  return weight >= 30 && weight <= 250;
    if (step === 3)  return height >= 120 && height <= 230;
    if (step === 4)  return !!form.gender;
    if (step === 5)  return !!form.activity;
    if (step === 6)  return !!form.goal;
    if (step === 7)  return !!form.diet;
    if (step === 8)  return true;
    if (step === 9)  return !!form.meal_pattern;
    if (step === 10) return form.trackProgress !== null;
    if (step === 11) return true;
    return false;
  };

  const handleCalculate = async () => {
    if (!canContinue()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tdee/calculate`, {
        age: normalizeNum(form.age), weight: normalizeNum(form.weight),
        height: normalizeNum(form.height), gender: form.gender,
        activity: form.activity, goal: form.goal,
      });
      setResult(res.data);
      setStep(12);
    } catch {
      toast.error('Грешка при изчисляването. Опитайте отново.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step < 11) { setStep(s => s + 1); return; }
    if (step === 11) { handleCalculate(); }
  };

  const handleGeneratePlan = () => {
    const targets = result.targets;
    let cal;
    if (form.goal === 'maintain') cal = targets.maintain;
    else if (form.goal === 'lose') {
      cal = selectedPace === 'fast' ? targets.lose_fast : selectedPace === 'medium' ? targets.lose_medium : targets.lose_slow;
    } else {
      cal = selectedPace === 'fast' ? targets.gain_fast : selectedPace === 'medium' ? targets.gain_medium : targets.gain_slow;
    }
    const goalLabel = form.goal === 'lose' ? 'Отслабване' : form.goal === 'maintain' ? 'Поддържане' : 'Качване';
    const paceLabel = selectedPace === 'fast' ? 'Бързо' : selectedPace === 'medium' ? 'Умерено' : 'Бавно';
    localStorage.setItem('portion_tdee', JSON.stringify({
      target_calories: cal, goal: goalLabel, pace: paceLabel,
      diet: form.diet, gender: form.gender, age: form.age,
      weight: form.weight, height: form.height, activity: form.activity,
      tdee: result.tdee, bmr: result.bmr,
      excludedFoods: form.excludedFoods, meal_pattern: form.meal_pattern,
      trackedMetrics: form.trackedMetrics,
    }));
    if (user) {
      axios.put(`${API}/profile`, {
        gender: form.gender, age: Number(form.age), height: Number(form.height),
      }).catch(() => {});
    }
    if (!user) {
      setShowAuthModal(true);
    } else {
      sessionStorage.setItem('from_quiz', 'true');
      navigate('/my-plan');
    }  };

  const stepVariants = {
    enter:  { opacity: 0, y: 12 },
    center: { opacity: 1, y: 0  },
    exit:   { opacity: 0, y: -8 },
  };

  const foodGroups = getFoodGroups(form.diet);

  // ── Derived values for step 12 ──
  const firstName  = user?.full_name?.split(' ')[0] || null;
  const bmiInfo    = step === 12 ? getBmiInfo(normalizeNum(form.weight), normalizeNum(form.height)) : null;
  const macros     = result?.macros?.maintain;

  return (
    <>
      <SEOMeta
        title="TDEE Калкулатор — Изчисли Дневния Си Калориен Прием"
        description="Безплатен TDEE калкулатор за изчисляване на точния дневен калориен прием. Въведи възраст, тегло, ръст и активност. Получи персонализиран хранителен план."
        keywords="TDEE калкулатор, калорийна нужда, базален метаболизъм, BMR, дневни калории, хранителен план калкулатор"
        canonical="/calculator"
        breadcrumbs={QUIZ_BREADCRUMBS}
        schemas={[
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'Как да изчислиш TDEE и да получиш персонализиран хранителен план',
            description: 'Безплатен TDEE калкулатор — въведи данните си и получи персонализиран хранителен план в 2 минути.',
            totalTime: 'PT2M',
            step: [
              { '@type': 'HowToStep', position: 1, name: 'Избери своя пол', text: 'Избери мъж или жена за точни изчисления на базалния метаболизъм.' },
              { '@type': 'HowToStep', position: 2, name: 'Въведи възраст, тегло и ръст', text: 'Необходими данни за формулата Mifflin-St Jeor.' },
              { '@type': 'HowToStep', position: 3, name: 'Избери ниво на активност', text: 'От заседнал начин на живот до много активен (1.2–1.9 PAL коефициент).' },
              { '@type': 'HowToStep', position: 4, name: 'Избери цел', text: 'Отслабване, поддържане или качване на мускулна маса.' },
              { '@type': 'HowToStep', position: 5, name: 'Получи TDEE и хранителен план', text: 'Получаваш точния си дневен калориен прием и Gemini AI генерира 7-дневен план.' },
            ],
          },
        ]}
      />
      <PageBreadcrumb items={QUIZ_BREADCRUMBS} />

      <div className="max-w-lg mx-auto px-4 sm:px-6 py-10" data-testid="tdee-quiz">

        {/* Progress bar — steps 1–10 */}
        {step <= 10 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Стъпка {step} от {totalSteps}
              </span>
              <span className="text-sm text-slate-400 dark:text-slate-500">{STEP_NAMES[step - 1]}</span>
            </div>
            <Progress value={progress} className="h-2" data-testid="tdee-quiz-progress" />
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            variants={stepVariants}
            initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >

            {/* ── Step 1: Age ─────────────────────────────────────── */}
            {step === 1 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-5">
                  <StepIllustration step={1} />
                  <NumberInput label="Колко години си?" value={form.age}
                    onChange={v => setForm(f => ({...f, age: v}))}
                    min={16} max={80} unit="години"
                    why="Възрастта влияе върху основния метаболизъм." />
                </CardContent>
              </Card>
            )}

            {/* ── Step 2: Weight ───────────────────────────────────── */}
            {step === 2 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-5">
                  <StepIllustration step={2} />
                  <NumberInput label="Колко тежиш?" value={form.weight}
                    onChange={v => setForm(f => ({...f, weight: v}))}
                    min={30} max={250} unit="кг"
                    why="Теглото помага за изчисляване на BMR." />
                </CardContent>
              </Card>
            )}

            {/* ── Step 3: Height ───────────────────────────────────── */}
            {step === 3 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-5">
                  <StepIllustration step={3} />
                  <NumberInput label="Колко си във височина?" value={form.height}
                    onChange={v => setForm(f => ({...f, height: v}))}
                    min={120} max={230} unit="см"
                    why="Ръстът е необходима част от формулата за основен метаболизъм (BMR)." />
                </CardContent>
              </Card>
            )}

            {/* ── Step 4: Gender ───────────────────────────────────── */}
            {step === 4 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-4">
                  <StepIllustration step={4} />
                  <p className="text-base font-medium text-gray-900 dark:text-gray-100">Какъв е полът ти?</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><Info className="w-3 h-3" />Полът влияе на BMR с около 5–16%.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <RadioOption val="male"   label="Мъж"  current={form.gender} onChange={v => setForm(f => ({...f, gender: v}))} />
                    <RadioOption val="female" label="Жена" current={form.gender} onChange={v => setForm(f => ({...f, gender: v}))} />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ── Step 5: Activity ─────────────────────────────────── */}
            {step === 5 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-3">
                  <StepIllustration step={5} />
                  <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">Какво е нивото ти на активност?</p>
                  {ACTIVITIES.map(a => (
                    <RadioOption key={a.value} val={a.value} label={a.label} desc={a.desc}
                      current={form.activity} onChange={v => setForm(f => ({...f, activity: v}))} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* ── Step 6: Goal ─────────────────────────────────────── */}
            {step === 6 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-3">
                  <StepIllustration step={6} />
                  <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">Каква е целта ти?</p>
                  {GOALS.map(g => (
                    <RadioOption key={g.value} val={g.value} label={g.label} desc={g.desc}
                      current={form.goal} onChange={v => setForm(f => ({...f, goal: v}))} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* ── Step 7: Diet ─────────────────────────────────────── */}
            {step === 7 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-3">
                  <StepIllustration step={7} />
                  <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">Какъв хранителен режим следваш?</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Info className="w-3 h-3" />Ще съобразим хранителния ти план с предпочитанията ти.
                  </p>
                  {DIETS.map(d => (
                    <RadioOption key={d.value} val={d.value} label={d.label} desc={d.desc}
                      current={form.diet} onChange={v => setForm(f => ({...f, diet: v}))} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* ── Step 8: Food Exclusions ───────────────────────────── */}
            {step === 8 && (
              <Card className="card-shadow rounded-xl" data-testid="food-exclusion-step">
                <CardContent className="p-6 space-y-5">
                  <StepIllustration step={8} />
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className="text-base font-medium text-gray-900 dark:text-gray-100">Какви храни не консумираш?</p>
                      <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full mt-0.5">
                        Незадължително
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 flex items-start gap-1.5">
                      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      Цъкни на храните, които не искаш в плана си. Можеш да пропуснеш тази стъпка с „Напред".
                    </p>
                  </div>
                  {foodGroups.map(group => (
                    <div key={group.label}>
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${group.headerClass}`}>{group.label}</span>
                        <div className={`h-px flex-1 ${group.dividerBg}`} />
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {group.foods.map(food => (
                          <FoodChip key={food} food={food} excluded={form.excludedFoods.includes(food)} onToggle={toggleFood} />
                        ))}
                      </div>
                    </div>
                  ))}
                  {form.excludedFoods.length > 0 && (
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-slate-700">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Изключени: <span className="font-semibold text-rose-500 dark:text-rose-400">{form.excludedFoods.length} храни</span>
                      </p>
                      <button type="button" data-testid="clear-exclusions-btn"
                        onClick={() => setForm(f => ({ ...f, excludedFoods: [] }))}
                        className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors underline underline-offset-2">
                        Изчисти всички
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ── Step 9: Meal Pattern ──────────────────────────────── */}
            {step === 9 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-3">
                  <StepIllustration step={9} />
                  <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">Брой хранения на ден?</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Info className="w-3 h-3" />Ще разпределим калориите спрямо избрания режим.
                  </p>
                  {MEAL_PATTERNS.map(mp => (
                    <RadioOption key={mp.value} val={mp.value} label={mp.label} desc={mp.desc}
                      current={form.meal_pattern} onChange={v => setForm(f => ({...f, meal_pattern: v}))} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* ── Step 10: Progress Tracking ────────────────────────── */}
            {step === 10 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-5">
                  <StepIllustration step={10} />
                  <div>
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
                      Искаш ли да следиш физическите си измервания?
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Ще ти напомняме за седмичен отчет, за да проследиш прогреса си
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <RadioOption val="yes" label="Да" current={form.trackProgress}
                      onChange={v => setForm(f => ({ ...f, trackProgress: v }))} />
                    <RadioOption val="no" label="Не" current={form.trackProgress}
                      onChange={v => setForm(f => ({ ...f, trackProgress: v, trackedMetrics: [] }))} />
                  </div>
                  {form.trackProgress === 'yes' && (
                    <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                      <div>
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Какво друго искаш да следиш освен тегло?
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Избери до 2 опции</p>
                      </div>
                      <div className="flex flex-wrap gap-2" data-testid="metric-options">
                        {METRIC_OPTIONS.map(opt => {
                          const isSelected = form.trackedMetrics.includes(opt.value);
                          const isDisabled  = !isSelected && form.trackedMetrics.length >= 2;
                          return (
                            <button key={opt.value} type="button"
                              data-testid={`metric-chip-${opt.value}`}
                              disabled={isDisabled}
                              onClick={() => setForm(f => ({
                                ...f,
                                trackedMetrics: isSelected
                                  ? f.trackedMetrics.filter(m => m !== opt.value)
                                  : [...f.trackedMetrics, opt.value],
                              }))}
                              className={`px-3.5 py-2 rounded-xl text-sm font-medium border-2 transition-all duration-150 select-none ${
                                isSelected  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                                : isDisabled ? 'border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-300 dark:text-slate-600 cursor-not-allowed'
                                : 'border-[#E6EDF5] dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:border-emerald-300 dark:hover:border-emerald-500/60'
                              }`}>
                              {isSelected && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5 text-emerald-600 dark:text-emerald-400" />}
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      {form.trackedMetrics.length > 0 && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400">
                          Избрани:{' '}
                          {form.trackedMetrics.map(m => METRIC_OPTIONS.find(o => o.value === m)?.label).join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ── Step 11: Pace ─────────────────────────────────────── */}
            {step === 11 && (
              <Card className="card-shadow rounded-xl">
                <CardContent className="p-6 space-y-3">
                  <StepIllustration step={11} />
                  <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {form.goal === 'maintain' ? 'Изчисляваме TDEE…' : 'Какво темпо?'}
                  </p>
                  {form.goal === 'maintain' ? (
                    <p className="text-sm text-slate-500">
                      Избрахте цел „Поддържане на тегло" — ще изчислим дневния ти разход без калориен дефицит.
                      Натиснете „Изчисли", за да продължим.
                    </p>
                  ) : (
                    PACES.map(p => (
                      <RadioOption key={p.value} val={p.value} label={p.label} desc={p.desc}
                        current={selectedPace} onChange={setSelectedPace} />
                    ))
                  )}
                </CardContent>
              </Card>
            )}

            {/* ── Step 12: Personalized Welcome / Results ────────────── */}
            {step === 12 && result && (
              <div className="space-y-4" data-testid="tdee-result-card">

                {/* Hero header */}
                <motion.div
                  className="text-center pt-2 pb-1"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-3xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg,#34d399,#059669)', boxShadow: '0 12px 32px #34d39940' }}>
                    <Award className="w-10 h-10 text-white" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {firstName ? `Готов си, ${firstName}!` : 'Готово!'}
                  </h2>
                  <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">
                    Ето персонализираните ти данни
                  </p>
                </motion.div>

                {/* TDEE Big Card */}
                <motion.div
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 }}
                >
                  <div className="rounded-2xl overflow-hidden relative"
                    style={{ background: 'linear-gradient(135deg,#10b981 0%,#065f46 100%)', boxShadow: '0 16px 40px #10b98130' }}>
                    <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-white/10 pointer-events-none" />
                    <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/5 pointer-events-none" />
                    <div className="p-6 text-center relative">
                      <p className="text-emerald-200 text-xs font-bold uppercase tracking-widest mb-2">
                        Твоят дневен TDEE
                      </p>
                      <p className="text-7xl font-black text-white tabular-nums leading-none">
                        {result.tdee}
                      </p>
                      <p className="text-emerald-300 mt-1">ккал/ден</p>
                      <p className="text-emerald-400/70 text-xs mt-2">
                        Базален метаболизъм (BMR): {result.bmr} ккал
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Macro Breakdown */}
                {macros && (
                  <motion.div
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 text-center mb-3">
                      Макро разпределение
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Белтъци',       value: macros.proteins, unit: 'г', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)'  },
                        { label: 'Въглехидрати',  value: macros.carbs,    unit: 'г', color: '#60a5fa', bg: 'rgba(96,165,250,0.08)'  },
                        { label: 'Мазнини',       value: macros.fats,     unit: 'г', color: '#34d399', bg: 'rgba(52,211,153,0.08)'  },
                      ].map(m => (
                        <div key={m.label} className="rounded-2xl p-3 text-center border border-gray-100 dark:border-slate-700/60"
                          style={{ backgroundColor: m.bg }}>
                          <p className="text-2xl font-bold tabular-nums" style={{ color: m.color }}>{m.value}</p>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500">{m.unit}/ден</p>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mt-0.5">{m.label}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* BMI Indicator */}
                {bmiInfo && (
                  <motion.div
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.28 }}
                  >
                    <Card className="rounded-2xl border border-gray-100 dark:border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-0.5">
                              ИТМ (BMI)
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                              {bmiInfo.bmi}
                            </p>
                          </div>
                          <span className="text-sm font-semibold px-3 py-1.5 rounded-full"
                            style={{ backgroundColor: `${bmiInfo.color}22`, color: bmiInfo.color }}>
                            {bmiInfo.category}
                          </span>
                        </div>
                        {/* BMI bar */}
                        <div className="h-2 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{ backgroundColor: bmiInfo.color }}
                            initial={{ width: 0 }}
                            animate={{ width: `${bmiInfo.pct}%` }}
                            transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                          />
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-[10px] text-slate-300 dark:text-slate-600">15</span>
                          <span className="text-[10px] text-slate-300 dark:text-slate-600">40+</span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Pace selector */}
                {form.goal !== 'maintain' && (
                  <motion.div
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="space-y-3"
                  >
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Избери скорост:</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { key: 'slow',   label: 'Бавно',   cal: form.goal==='lose'?result.targets.lose_slow:result.targets.gain_slow,     desc: '~0.15кг/седм', testid: 'tdee-speed-slow'   },
                        { key: 'medium', label: 'Умерено', cal: form.goal==='lose'?result.targets.lose_medium:result.targets.gain_medium,  desc: '~0.3кг/седм',  testid: 'tdee-speed-medium' },
                        { key: 'fast',   label: 'Бързо',   cal: form.goal==='lose'?result.targets.lose_fast:result.targets.gain_fast,     desc: '~0.5кг/седм',  testid: 'tdee-speed-fast'   },
                      ].map(p => (
                        <button key={p.key} data-testid={p.testid} onClick={() => setSelectedPace(p.key)}
                          className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                            selectedPace === p.key
                              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 shadow-sm shadow-emerald-500/20'
                              : 'border-[#E6EDF5] dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-emerald-300 dark:hover:border-emerald-500/60'
                          }`}>
                          <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{p.label}</p>
                          <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums mt-1">{p.cal}</p>
                          <p className="text-xs text-slate-400 dark:text-slate-500">ккал</p>
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{p.desc}</p>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Maintain goal display */}
                {form.goal === 'maintain' && (
                  <motion.div
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                  >
                    <Card className="card-shadow rounded-xl">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Цел: Поддържане на тегло</p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                          {result.targets.maintain} ккал/ден
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Excluded foods summary */}
                {form.excludedFoods.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-3 bg-rose-50 dark:bg-rose-900/20 rounded-xl border border-rose-100 dark:border-rose-700/30">
                    <p className="w-full text-xs font-semibold text-rose-600 dark:text-rose-400 mb-1">Изключени от плана:</p>
                    {form.excludedFoods.map(f => (
                      <span key={f} className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-rose-200 dark:border-rose-700/40 text-rose-500 dark:text-rose-400 text-xs rounded-full line-through">
                        {f}
                      </span>
                    ))}
                  </div>
                )}

                {/* CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                >
                  <Button onClick={handleGeneratePlan}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-13 text-base rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200 hover:shadow-emerald-500/40 hover:-translate-y-0.5"
                    data-testid="tdee-generate-plan-button"
                  >
                    Генерирай хранителен план
                    <ChevronRight className="w-5 h-5 ml-1.5" />
                  </Button>
                </motion.div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons — steps 1–11 */}
        {step <= 11 && (
          <div className="flex items-center justify-between mt-8">
            <Button variant="outline" onClick={() => setStep(s => Math.max(1, s-1))} disabled={step === 1}
              data-testid="tdee-quiz-back-button"
              className="border-gray-300 dark:border-slate-600 text-gray-900 dark:text-gray-100 dark:bg-transparent dark:hover:bg-slate-700">
              <ChevronLeft className="w-4 h-4 mr-1" /> Назад
            </Button>
            <Button onClick={handleNext} disabled={!canContinue() || loading}
              data-testid="tdee-quiz-next-button"
              className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-11">
              {loading
                ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                : <>{step === 11 ? 'Изчисли' : 'Напред'} <ChevronRight className="w-4 h-4 ml-1" /></>
              }
            </Button>
          </div>
        )}
      </div>

      {/* Auth modal — shown when unauthenticated user clicks "Generate plan" */}
      <AnimatePresence>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </AnimatePresence>
    </>
  );
}
